import type { LoaderFunctionArgs, RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { lazyElement } from '../routing/lazyRoute';

const mobileDeviceRoutes: RouteObject[] = [
  {
    path: '/mobile-device',
    element: <Outlet />,
    handle: {
      documentTitle: 'Mobile Devices',
      crumb: () => ({ label: 'Mobile Devices', to: '/mobile-device' }),
    },
    children: [
      {
        index: true,
        element: lazyElement(() => import('./list/List')),
      },
      {
        path: 'unlocked',
        element: lazyElement(() => import('./UnlockedList')),
        handle: {
          documentTitle: 'Unlocked Devices',
          crumb: () => ({ label: 'Unlocked', to: '/mobile-device/unlocked' }),
        },
      },
      {
        id: 'mobileDeviceView',
        path: 'view/:id',
        element: lazyElement(() => import('./detail-view/MobileDeviceDetailView')),
        loader: async ({ params }: LoaderFunctionArgs) => {
          const { default: api } = await import('../common/api');
          const id = params.id;
          if (!id) {
            throw new Response('Missing id', { status: 400 });
          }
          try {
            const res = await api.get(`/mobile-device/${id}`);
            const data = (res.data as { data?: unknown } | undefined)?.data;
            if (data == null) {
              throw new Response('Missing mobile device data', { status: 502 });
            }
            return data;
          } catch (e: unknown) {
            if ((e as { response?: { status?: number } })?.response?.status === 404) {
              throw new Response(`Mobile device with ID ${id} not found`, { status: 404 });
            }
            throw e;
          }
        },
        handle: {
          crumb: (m: unknown) => {
            const match = m as { data?: unknown };
            const data = match.data as
              | {
                  id?: number | string;
                  name?: string;
                  deviceName?: string;
                  serialNumber?: string;
                }
              | undefined;
            const label =
              data?.name ??
              data?.deviceName ??
              (data?.serialNumber ? `Device ${data.serialNumber}` : 'Mobile Device');
            const id = data?.id;
            return {
              label,
              to: id != null ? `/mobile-device/view/${id}` : undefined,
            };
          },
        },
      },
    ],
  },
  {
    path: '/mobile-device/status',
    element: lazyElement(() => import('./status/Status')),
    handle: {
      documentTitle: 'Mobile Device Statuses',
      crumb: () => ({ label: 'Status', to: '/mobile-device/status' }),
    },
  },
  {
    path: '/profiles',
    element: lazyElement(() => import('./profile/ProfilesList')),
    handle: {
      documentTitle: 'Mobile Device Profiles',
      crumb: () => ({ label: 'Profiles', to: '/profiles' }),
    },
  },
  {
    path: '/mobile-device/needs-inspection',
    element: lazyElement(() => import('./NeedsInspection')),
    handle: {
      documentTitle: 'Devices Needing Inspection',
      crumb: () => ({ label: 'Needs Inspection', to: '/mobile-device/needs-inspection' }),
    },
  },
  {
    path: '/mobile-device/monitoring',
    element: lazyElement(() => import('./Monitoring')),
    handle: {
      documentTitle: 'Device Monitoring',
      crumb: () => ({ label: 'Monitoring', to: '/mobile-device/monitoring' }),
    },
  },
  {
    path: '/mobile-device/app-management',
    element: lazyElement(() => import('./AppManagement')),
    handle: {
      documentTitle: 'App Management',
      crumb: () => ({ label: 'App Management', to: '/mobile-device/app-management' }),
    },
  },
];

export default mobileDeviceRoutes;
